import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  AppBar, 
  Toolbar, 
  Button, 
  Avatar, 
  Menu, 
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import TreeIcon from '../Icons/TreeIcon';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import HistoryIcon from '@mui/icons-material/History';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import FolderIcon from '@mui/icons-material/Folder';
import ContactMailIcon from '@mui/icons-material/ContactMail';

const NavContainer = styled.div`
  position: fixed;
  top: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 1100;
  padding: 0 20px;
`;

const StyledAppBar = styled(AppBar)`
  background-color: rgba(var(--primary-color), 0.9);
  backdrop-filter: blur(10px);
  box-shadow: var(--box-shadow);
  width: 95%;
  max-width: 1400px;
  border-radius: 15px;
  position: relative;
  font-family: 'Cormorant Garamond', Calibri, serif;
`;

const StyledToolbar = styled(Toolbar)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--background-color);
  padding: 0.25rem 0.75rem;
  gap: 1rem;
  min-height: 56px;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;

  .logo-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const MenuSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  justify-content: center;
  margin: 0 0.5rem;

  @media (max-width: 900px) {
    display: none;
  }
`;

const MenuButton = styled(Button)`
  color: var(--background-color) !important;
  text-transform: none !important;
  font-size: 0.9rem !important;
  padding: 4px 8px !important;
  min-width: 0 !important;
  white-space: nowrap;
  font-family: 'Cormorant Garamond', Calibri, serif !important;
  letter-spacing: 0.2px;
  
  .MuiButton-startIcon {
    margin-right: 4px;
  }
  
  &:hover {
    background-color: rgba(var(--accent-color), 0.1);
  }
`;

const SiteName = styled.h1`
  font-size: 2.5rem;
  color: var(--background-color);
  margin: 0;
  font-family: 'Tangerine', Calibri, cursive;
  font-weight: 700;
  letter-spacing: 0.5px;
  
  @media (max-width: 600px) {
    font-size: 2rem;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const WelcomeText = styled.span`
  color: var(--background-color);
  cursor: pointer;
  
  @media (max-width: 600px) {
    display: none;
  }
`;

const StyledAvatar = styled(Avatar)`
  cursor: pointer;
  background-color: ${props => props.color || 'var(--primary-color)'};
`;

const DrawerContent = styled.div`
  width: 280px;
  padding: 20px 0;
  background-color: var(--background-color);
  height: 100%;
  font-family: 'Cormorant Garamond', serif;
`;

const StyledListItem = styled(ListItem)`
  padding: 4px 16px;
  margin: 4px 8px;
  border-radius: 8px;
  color: var(--primary-color);
`;

const StyledListItemButton = styled(ListItemButton)`
  border-radius: 8px;
  padding: 8px 16px;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.1rem;
  
  &:hover {
    background-color: rgba(var(--primary-color), 0.1);
  }
`;

const UserSection2 = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  font-family: 'Cormorant Garamond', serif;
`;

const UserName = styled.div`
  color: var(--primary-color);
  font-family: 'Tangerine', Calibri, cursive;
  font-size: 2.2rem;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const StyledDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    background-color: var(--background-color);
    backdrop-filter: blur(10px);
  }
`;

const menuItems = [
  { text: 'Home', icon: <HomeIcon />, path: '/' },
  { text: 'A Brief History', icon: <HistoryIcon />, path: '/brief-history' },
  { text: 'Family Tree', icon: <TreeIcon />, path: '/family-tree' },
  { text: 'Family Connections', icon: <ConnectWithoutContactIcon />, path: '/family-connections' },
  { text: 'Proud Service', icon: <MilitaryTechIcon />, path: '/proud-service' },
  { text: 'Resources', icon: <FolderIcon />, path: '/resources' },
  { text: 'Contact Us', icon: <ContactMailIcon />, path: '/contact' },
];

const Navigation: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAccountClick = () => {
    handleMenuClose();
    navigate('/account');
  };

  const handleLogout = () => {
    handleMenuClose();
    navigate('/');
    logout();
  };

  const toggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    toggleDrawer(false);
  };

  return (
    <NavContainer>
      <StyledAppBar>
        <StyledToolbar>
          <LogoSection>
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => toggleDrawer(true)}
              sx={{ display: { sm: 'flex', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <div className="logo-content" onClick={() => navigate('/')}>
              <TreeIcon sx={{ fontSize: '2rem' }} />
              <SiteName>Hedgcorth.com</SiteName>
            </div>
          </LogoSection>

          <MenuSection>
            {menuItems.map((item) => (
              <MenuButton
                key={item.text}
                onClick={() => handleNavigation(item.path)}
                startIcon={item.icon}
              >
                {item.text}
              </MenuButton>
            ))}
          </MenuSection>

          {user ? (
            <UserSection>
              <UserInfo onClick={handleMenuOpen}>
                <WelcomeText>Welcome, {user.firstName}</WelcomeText>
                <StyledAvatar color={user.avatar}>
                  {user.firstName && user.lastName 
                    ? `${user.firstName[0]}${user.lastName[0]}`
                    : 'U'}
                </StyledAvatar>
              </UserInfo>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  style: {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                  }
                }}
              >
                <MenuItem onClick={handleAccountClick}>My Account</MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </UserSection>
          ) : (
            <Button
              color="primary"
              component={Link}
              to="/login"
              variant="outlined"
            >
              Login
            </Button>
          )}
        </StyledToolbar>

        <StyledDrawer
          anchor="left"
          open={drawerOpen}
          onClose={() => toggleDrawer(false)}
        >
          <DrawerContent>
            {user && (
              <>
                <UserSection2>
                  <StyledAvatar color={user.avatar}>
                    {user.firstName && user.lastName 
                      ? `${user.firstName[0]}${user.lastName[0]}`
                      : 'U'}
                  </StyledAvatar>
                  <div>
                    <UserName>Welcome, {user.firstName}</UserName>
                    <Button
                      size="small"
                      onClick={handleAccountClick}
                    >
                      My Account
                    </Button>
                  </div>
                </UserSection2>
                <Divider />
              </>
            )}
            <List>
              {menuItems.map((item, index) => (
                <React.Fragment key={item.text}>
                  <StyledListItem disablePadding>
                    <StyledListItemButton onClick={() => handleNavigation(item.path)}>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </StyledListItemButton>
                  </StyledListItem>
                  {index < menuItems.length - 1 && <Divider variant="middle" />}
                </React.Fragment>
              ))}
            </List>
            {user && (
              <>
                <Divider />
                <StyledListItem disablePadding>
                  <StyledListItemButton onClick={handleLogout}>
                    <ListItemText primary="Logout" />
                  </StyledListItemButton>
                </StyledListItem>
              </>
            )}
          </DrawerContent>
        </StyledDrawer>
      </StyledAppBar>
    </NavContainer>
  );
};

export default Navigation;